import React, { useEffect, useState } from "react"
import moment from "moment-timezone"
import { Link } from "react-router-dom"
import { Alert, Badge, Button, ModalBody } from "reactstrap"
import { get } from "helpers/api_helper"
import DraftModal from "./DraftModal"
import ParcelSelector, { documentsOnly, initialSelection, parcelWeights, selectionBody, selectionCheck } from "./ParcelSelector"
import { blockerText, shipmentName } from "./shipmentLabels"
import { UNCONFIRMED_CHANGE, logisticsReadMessage } from "./useLogisticsOperation"

// When the courier picks up an AWB generated now (US 107), as Events estimates it without Saturday delivery and with it (`saturday`):
// "today, before 17:30", "tomorrow" or "Monday, 7 Dec 2026".
const pickupDay = ({ day, withSaturdayDelivery }, saturday) => (saturday && withSaturdayDelivery ? withSaturdayDelivery : day)
const pickupLabel = (pickup, today, saturday) => {
  const day = moment(pickupDay(pickup, saturday), "YYYY-MM-DD")
  const days = day.diff(moment(today, "YYYY-MM-DD"), "days")
  return `Estimated pickup: ${days === 0 ? `today, before ${pickup.closing}` : days === 1 ? "tomorrow" : day.format("dddd, D MMM YYYY")}`
}

// The address in Cargus's nomenclature (US 100, ticket 20 Q10): the locality found in its county, or not, with the ones Cargus may mean,
// each taken on the shipment's copy of its address with a click, unless the copy is locked (`onCorrectLocality` none: the main shipment of a
// validated sheet, whose address changes once the sheet is reopened); a postal code that does not match the street's only warns; an extended
// zone and no Saturday delivery are information.
function NomenclatureCheck({ address, check, pending, onCorrectLocality }) {
  const { locality, postalCode, extendedZone, saturdayDelivery } = check
  return <div className="small mb-2" role="group" aria-label="Nomenclature check (Cargus)">
    <div className="text-muted mb-1">Nomenclature check (Cargus)</div>
    {locality.found ? <p className="mb-1"><i className="bx bx-check text-success mr-1" aria-hidden="true" />
      {`Locality “${address.locality}” found in county ${address.county}`}</p>
      : <div className="mb-1 text-danger">
        <p className="mb-1"><i className="bx bx-x-circle mr-1" aria-hidden="true" />
          {`Locality “${address.locality}” is not in county ${address.county} in Cargus's list.${locality.suggestions.length ? " Did you mean:" : ""}`}</p>
        {onCorrectLocality ? locality.suggestions.map(name => <Button key={name} color="light" size="sm" className="mr-2 mb-1" disabled={pending}
          onClick={() => onCorrectLocality(name)}>{`Use “${name}”`}</Button>)
          : locality.suggestions.length > 0 && <p className="mb-1">{locality.suggestions.join(", ")}</p>}
        {!onCorrectLocality ? <p className="mb-0">Reopen the sheet to correct the address, then validate it again.</p>
          : !locality.suggestions.length && <p className="mb-0">Correct the locality in the address first.</p>}
      </div>}
    {postalCode.matches === false && <p className="mb-1 text-warning"><i className="bx bx-error mr-1" aria-hidden="true" />
      {`Postal code ${address.postalCode} is not the street's (${postalCode.expected} expected): you can continue.`}</p>}
    {extendedZone && <p className="mb-1"><i className="bx bx-info-circle text-info mr-1" aria-hidden="true" />Extended zone: Cargus charges extra kilometres.</p>}
    {saturdayDelivery === false && <p className="mb-1"><i className="bx bx-info-circle text-info mr-1" aria-hidden="true" />No Saturday delivery here.</p>}
  </div>
}

const CHECK_FAILED = "The address could not be checked in Cargus's nomenclature now."

// A nomenclature check that failed (`code`, or `true` without an answer). The check is a read: Cargus unreachable, an unknown code or no
// answer is checked again with Retry; a refusal the map knows, which a retry would get again, is shown as it is.
function CheckFailure({ code, onRetry }) {
  const known = logisticsReadMessage(code, null)
  return <Alert color="warning" className="small">{known || CHECK_FAILED}
    {(!known || code === "LOGISTICS_COURIER_UNAVAILABLE") && <> <Button color="link" className="p-0 align-baseline" onClick={onRetry}>Retry</Button></>}
  </Alert>
}

// What the generation answered when it did not save: Cargus's reasons when it refused the AWB, otherwise why it could not.
function Failure({ result }) {
  if (result.status === "unknown") return <Alert color="warning" className="mt-3 mb-0" role="alert">{UNCONFIRMED_CHANGE}</Alert>
  if (result.code === "LOGISTICS_COURIER_REFUSED") return <Alert color="danger" className="mt-3 mb-0" role="alert">
    <p className="mb-1">Cargus refused the AWB: nothing was created. Correct what it names, then generate it again.</p>
    <ul className="mb-0">{(result.details?.messages || []).map(message => <li key={message}>{message}</li>)}</ul>
  </Alert>
  return <Alert color="danger" className="mt-3 mb-0" role="alert">{result.message}</Alert>
}

// „Generate AWB” (US 98–107, ticket 20): for a shipment in preparation with a Romanian address, its AWB created through Cargus with the
// parcels chosen, their weights split equally and corrected, an envelope only for documents, and Saturday delivery. A successful
// generation is the shipment's processing (D14, D19); the dialog is bound to the preview it read. Before it, the address is
// checked in Cargus's nomenclature: a locality Cargus does not have blocks the generation, and one it suggests is taken on the shipment's
// copy of its address (`onCorrectLocality`). What blocks a courier is shown instead of the parcels. A supplementary shipment (`shipment`)
// takes its own address and contact. Parcels typed are kept until they are discarded (DraftModal).
export default function GenerateAwbDialog({ eventId, sheet, shipment = null, pending, onSubmit, onCorrectLocality, onClose }) {
  const [read, setRead] = useState({ loading: true })
  const [attempt, setAttempt] = useState(0)
  const [start, setStart] = useState(null)
  const [selection, setSelection] = useState(null)
  const [result, setResult] = useState(null)
  const shipmentId = shipment?.id
  const { address, contact } = (shipment ? shipment : sheet.delivery) || {}
  // A corrected address is checked again.
  const where = JSON.stringify(address || null)
  useEffect(() => {
    let active = true
    setRead(previous => ({ ...previous, loading: true, error: false }))
    const params = shipmentId ? [{ params: { shipment: shipmentId } }] : []
    const base = `/admin/logistics/events/${eventId}/shipments`
    Promise.all([get(`${base}/validation`, ...params), get("/admin/logistics/settings"),
      get(`${base}/address-check`, ...params).then(response => response.data, error => ({ failed: error?.response?.data?.message || true }))])
      .then(([preview, settings, checked]) => {
        if (!active) return
        const documents = documentsOnly(preview.data.shipment)
        setRead({ preview: preview.data, packages: settings.data.packages, documents, checked })
        const first = initialSelection(settings.data.packages, { weights: parcelWeights(preview.data.shipment), documents })
        setStart(previous => previous || first)
        setSelection(previous => previous || first)
      }).catch(() => { if (active) setRead({ error: true }) })
    return () => { active = false }
  }, [eventId, shipmentId, attempt, where])

  const { preview, packages = [], documents, checked } = read
  const blockers = preview?.blockers.courier || []
  const located = Boolean(checked?.check?.locality.found)
  const ready = preview && !blockers.length && located && selection && selectionCheck(selection, packages).valid
  const dirty = Boolean(start) && JSON.stringify(selection) !== JSON.stringify(start)
  const generate = () => {
    setResult(null)
    onSubmit({ action: "generateAwb", revision: preview.revision, ...selectionBody(selection) }).then(answer => {
      if (answer && !["saved", "cancelled"].includes(answer.status)) setResult(answer)
    })
  }

  return <DraftModal id="logistics-generate-title" title={`Generate AWB — ${shipmentName(shipment)}`} size="lg" dirty={dirty} onClose={onClose}
    actions={<Button color="primary" disabled={!ready || pending} onClick={generate}>
      <i className="bx bx-barcode mr-1" aria-hidden="true" />{pending ? "Generating…" : "Generate AWB"}
    </Button>}>
    <ModalBody>
      <p className="small text-muted">{`Cargus creates the AWB: that is the shipment's processing, and ${shipment ? "it becomes" : "the sheet becomes"} ready to `
        + "be shipped; the stock decreases when it leaves. A generated AWB is not edited: to change it, undo the processing and generate another one, "
        + "which deletes it at Cargus while the courier has not scanned the parcel."}</p>
      {read.loading && !preview && <p role="status">Reading the shipment, the packages and the address…</p>}
      {read.error && <Alert color="danger">The shipment could not be read. <Button color="link" className="p-0 align-baseline"
        onClick={() => setAttempt(value => value + 1)}>Retry</Button></Alert>}
      {address && contact && <div className="small mb-2">
        <div className="text-muted">Recipient</div>
        <div>{`${address.recipient} · ${contact.name}, ${contact.courierPhone || contact.phone}, ${contact.email}`}</div>
      </div>}
      {checked?.check && <NomenclatureCheck address={address} check={checked.check} pending={pending} onCorrectLocality={onCorrectLocality} />}
      {checked?.failed && <CheckFailure code={checked.failed} onRetry={() => setAttempt(value => value + 1)} />}
      {blockers.length > 0 && <Alert color="danger" className="small">
        <p className="mb-1">A courier cannot take this shipment yet:</p>
        <ul className="mb-0">{blockers.map(blocker => <li key={blocker.kind}>{blockerText(blocker, sheet)}</li>)}</ul>
      </Alert>}
      {preview && !blockers.length && (selection?.parcels.length ? <ParcelSelector packages={packages} selection={selection} onChange={setSelection}
        weights={parcelWeights(preview.shipment)} documents={documents} />
        : <Alert color="warning" className="small">No shipping package is active. Add one in <Link to="/logistics/settings">Logistics → Settings</Link>.</Alert>)}
      {checked?.pickup && preview && <div className="mt-2">
        <Badge color="soft-primary" className="p-2">{pickupLabel(checked.pickup, preview.today, selection?.saturday)}</Badge>
        {pickupDay(checked.pickup, selection?.saturday) !== preview.today && <p className="small text-muted mt-1 mb-0">
          {`The day's pickup order closes at ${checked.pickup.closing}; Cargus picks up on weekdays, and on Saturday for Saturday delivery.`}
        </p>}
      </div>}
      {result && <Failure result={result} />}
    </ModalBody>
  </DraftModal>
}
